import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import {
  compose,
  withAuthHeader,
  withRetries,
  withAccountHeader,
  withE2ERequestStubbing,
  withConfiguredBaseUrl,
} from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { ServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import rateLimit from 'axios-rate-limit';
import urls from '@lifeomic/phc-web-toolkit/src/util/urls';
import queryString from 'query-string';
import _cloneDeep from 'lodash/cloneDeep';
import _flatten from 'lodash/flatten';

import {
  PaginationStrategy,
  singlePageStrategy,
} from './util/fhirSearchPagination';

const MAX_CONCURRENT_REQUESTS = 5;

export type EndpointTypes =
  | 'dsl'
  | 'fhir'
  | 'fhir-search'
  | 'terminology-search'
  | 'gene-set'
  | 'survey-service'
  | 'cohorts'
  | 'subject-ids'
  | 'graphQl'
  | 'ontology-code-search';

export class SearchCache {
  private _cache: { [key: string]: Promise<AxiosResponse<any> | any> };

  constructor() {
    this._cache = {};
  }

  maybeServeFromCache = async <TResult>(
    endpoint: EndpointTypes,
    cacheKey: string,
    fetchOperation: () => Promise<TResult>,
  ): Promise<TResult> => {
    const endpointBoundKey = `${endpoint}-${cacheKey}`;
    if (!this._cache[endpointBoundKey]) {
      this._cache[endpointBoundKey] = fetchOperation();
    }

    return this._cache[endpointBoundKey];
  };
}

export type ProjectSurveysResponse = {
  items: fhir.Questionnaire[];
};

export type FhirSubjectSearchResponse<TResource> = {
  entry: {
    resource: TResource;
  }[];
};

type FhirSearchReturnType = {
  aggregations: {
    results: {
      after_key?: {
        subjects: string;
      };
      buckets: {
        key: {
          subjects: string;
        };
        doc_count: number;
      }[];
    };
  };
};

const getLastAfterKey = (response: FhirSearchReturnType) =>
  response?.aggregations?.results?.after_key;

export const subjectsPaginationHandler: PaginationStrategy<FhirSearchReturnType> =
  {
    hasNextPage: response => !!getLastAfterKey(response),
    buildNextQuery: (lastResponse: FhirSearchReturnType, query: any) => {
      const afterKey = getLastAfterKey(lastResponse);
      const clonedQuery = _cloneDeep(query);
      clonedQuery.columns[0].aggregations.results.composite.after = afterKey;
      return clonedQuery;
    },
    mergeResponses: responses => {
      const allBuckets = responses.map(_ => _?.aggregations?.results?.buckets);
      const refResponse = _cloneDeep(responses[0]);
      refResponse.aggregations.results.buckets =
        _flatten(allBuckets).filter(Boolean);
      return refResponse;
    },
  };

export type FhirSearchDistinctReturnType = {
  results: string[];
  after?: undefined | string;
};

export const fhirSearchDistinctResultsPaginationStrategy: PaginationStrategy<FhirSearchDistinctReturnType> =
  {
    hasNextPage: (lastResponse: FhirSearchDistinctReturnType) => {
      return !!lastResponse.after;
    },
    buildNextQuery: (
      lastResponse: FhirSearchDistinctReturnType,
      originalQuery: any,
    ) => {
      return {
        ...originalQuery,
        after: lastResponse.after,
      };
    },
    mergeResponses: responses => {
      const list = responses.map(r => r.results).flat();
      return { results: list };
    },
  };

/**
 * This service is adapted from src/components/pages/SubjectSearch/Search/API/SubjectSearchService.ts
 * and simplified for general use, only keeping the basic search functionality
 */
export default class SubjectSearchService {
  private _searchCache: SearchCache;
  private getSubjectSearchAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(options?: Partial<AxiosRequestConfig>) {
    this._searchCache = new SearchCache();

    this.getSubjectSearchAPI = (auth: ServiceAuth) => {
      const baseInstance = compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create(options));

      return rateLimit(baseInstance, {
        maxRequests: MAX_CONCURRENT_REQUESTS,
      });
    };
  }

  fhirSubjectSearch =
    (auth: ServiceAuth) =>
    async <TResource>({
      projectId,
      cohort,
      body = {},
    }: {
      projectId: string;
      cohort?: string;
      body: Record<string, unknown>;
    }): Promise<AxiosResponse<FhirSubjectSearchResponse<TResource>>> => {
      const requestBody: any = {
        pageSize: 1000,
        _tag: `http://lifeomic.com/fhir/dataset|${projectId}`,
        ...body,
      };

      if (cohort) {
        requestBody.cohort = cohort;
      }

      const url = urls.api.fhir.patient({
        account: auth.account,
        isPostQuery: true,
      });
      return this._searchCache.maybeServeFromCache<
        AxiosResponse<FhirSubjectSearchResponse<TResource>>
      >('fhir', `${projectId}-${url}-${JSON.stringify(body)}`, async () =>
        this.getSubjectSearchAPI(auth).post<
          FhirSubjectSearchResponse<TResource>
        >(url, queryString.stringify(requestBody), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
        }),
      );
    };

  private _executePaginatedFSSQuery =
    (auth: ServiceAuth) =>
    async <TResult>({
      projectId,
      cohort,
      query,
      paginationHandler = singlePageStrategy,
      distinct = false,
    }: {
      projectId: string;
      cohort?: string;
      query: any;
      paginationHandler: PaginationStrategy<TResult>;
      distinct?: boolean;
    }): Promise<TResult> => {
      const responses: TResult[] = [];
      const getLastResponse = () => responses[responses.length - 1] || null;
      let currentResponse: TResult | null = null;

      do {
        const queryParams =
          paginationHandler?.getQueryParams?.(getLastResponse()) || null;
        const lastResponse = getLastResponse();
        const paginatedQuery = lastResponse
          ? paginationHandler.buildNextQuery(lastResponse, _cloneDeep(query))
          : query;
        let url = '';
        if (distinct) {
          url = cohort
            ? urls.api.fhirSearch.cohortDistinct(projectId, cohort, queryParams)
            : urls.api.fhirSearch.projectsDistinct(projectId, queryParams);
        } else {
          url = cohort
            ? urls.api.fhirSearch.cohort(projectId, cohort, queryParams)
            : urls.api.fhirSearch.projects(projectId, queryParams);
        }

        const pagedResponse = await this.getSubjectSearchAPI(
          auth,
        ).post<TResult>(url, paginatedQuery);

        if (pagedResponse.status === 408) {
          throw new Error('Request timed out');
        }

        responses.push(pagedResponse.data);
        currentResponse = getLastResponse();
      } while (
        currentResponse &&
        paginationHandler.hasNextPage(currentResponse)
      );

      return paginationHandler.mergeResponses(responses);
    };

  fhirSearch =
    (auth: ServiceAuth) =>
    async <TResult>({
      projectId,
      cohort,
      query,
      paginationHandler = singlePageStrategy,
    }: {
      projectId: string;
      cohort?: string;
      query: any;
      paginationHandler: PaginationStrategy<TResult>;
    }): Promise<TResult> =>
      this._searchCache.maybeServeFromCache<TResult>(
        'fhir-search',
        JSON.stringify(query),
        async () =>
          this._executePaginatedFSSQuery(auth)({
            projectId,
            cohort,
            query,
            paginationHandler,
          }),
      );
}
